import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight } from '../utils/theme';

interface ActivationData {
  readonly activated: boolean;
  readonly status: string;
  readonly gracePeriodEndsAt?: string;
  readonly waiverType?: string;
}

/** Simple event system so settings page can trigger a banner refresh. */
type Listener = () => void;
const listeners = new Set<Listener>();

/** refreshActivationBanner - notify all mounted ActivationBanner instances to re-fetch. */
export function refreshActivationBanner(): void {
  listeners.forEach(fn => fn());
}

/** ActivationBanner - shows warning when owner account needs activation or payment update. */
export default function ActivationBanner() {
  const { colors } = useTheme();
  const router = useRouter();
  const [data, setData] = useState<ActivationData | null>(null);

  const fetchStatus = useCallback(() => {
    api<ActivationData>('/api/owner/activation/status')
      .then(setData)
      .catch(() => { /* non-critical: banner hidden if fetch fails */ });
  }, []);

  useFocusEffect(useCallback(() => {
    fetchStatus();
    listeners.add(fetchStatus);
    return () => { listeners.delete(fetchStatus); };
  }, [fetchStatus]));

  if (!data || data.activated && data.status === 'active') return null;

  const isGrace = data.status === 'grace_period';
  const bgColor = isGrace ? '#fef3c7' : '#fee2e2';
  const textColor = isGrace ? '#92400e' : '#991b1b';
  const iconColor = isGrace ? '#d97706' : '#dc2626';
  const icon: keyof typeof Ionicons.glyphMap = isGrace ? 'warning' : 'alert-circle';

  const graceDate = data.gracePeriodEndsAt
    ? new Date(data.gracePeriodEndsAt).toLocaleDateString()
    : '';

  const message = isGrace
    ? `Payment failed. Update your card by ${graceDate}.`
    : 'Your account is not activated. Customers cannot purchase washes.';

  return (
    <TouchableOpacity
      style={[styles.banner, { backgroundColor: bgColor }]}
      onPress={() => router.push('/owner/settings' as never)}
      activeOpacity={0.7}
    >
      <Ionicons name={icon} size={18} color={iconColor} />
      <Text style={[styles.text, { color: textColor }]} numberOfLines={2}>{message}</Text>
      <Ionicons name="chevron-forward" size={16} color={textColor} />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  text: {
    flex: 1,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
  },
});
